import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard } from "../App"; // Assuming AuthGuard is correctly implemented
import { toast } from "react-toastify"; // Import ToastContainer and toast
import "react-toastify/dist/ReactToastify.css"; // Import toastify CSS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHistory, faCalendarAlt,faSpinner, faHome, faUserCircle, faEnvelope, faPhone, faMoneyBillWave, faCheckCircle, faTimesCircle, faCircle, faInfoCircle } from "@fortawesome/free-solid-svg-icons"; // Added relevant icons

const PreviousReservationsPage = () => {
  const [previousReservations, setPreviousReservations] = useState([]);
  const [loading, setLoading] = useState(true); // Add loading state
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  let userId = null;
  try {
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      userId = userData.unique;
    }
  } catch (e) {
    console.error("Error parsing user data from localStorage:", e);
    // Handle error, maybe clear localStorage or redirect to login
  }

  useEffect(() => {
    const fetchPreviousReservations = async () => {
      setLoading(true);
      if (!token || !userId) {
        toast.warn("Authentication details missing. Please log in.");
        setPreviousReservations([]);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/user/reservations/viewyourpreviousreservation/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // Ensure response.data.response is an array, handle cases where it might be a single object or null
        setPreviousReservations(Array.isArray(response.data.response) ? response.data.response : (response.data.response ? [response.data.response] : []));
      } catch (error) {
        console.error("Error fetching previous reservations:", error);
        setPreviousReservations([]); // Clear data on error
        if (error.response && error.response.data && error.response.data.response) {
          toast.error(error.response.data.response);
        } else {
          toast.error("Failed to load previous reservations. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPreviousReservations();
  }, [token, userId]); // Depend on token and userId to re-fetch if they change

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
      case "Paid":
      case "completed": // assuming 'completed' is also a green status
        return "bg-green-100 text-green-800";
      case "Pending":
      case "Awaiting Payment": // assuming this might be a yellow status
        return "bg-yellow-100 text-yellow-800";
      case "Rejected":
      case "Cancelled":
      case "Failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto py-8 text-center animate-fade-in-up">
          <div className="flex items-center justify-center mb-6">
            <FontAwesomeIcon icon={faHistory} className="text-blue-500 text-6xl mr-4" />
            <h1 className="text-5xl font-extrabold text-gray-900">
              Your Previous Reservations
            </h1>
          </div>
          <p className="text-gray-600 text-lg mb-6">
            Explore your past property bookings and their details.
          </p>
          <span className="text-indigo-600 text-2xl font-bold flex items-center justify-center sm:justify-start mb-10">
            <FontAwesomeIcon icon={faCalendarAlt} className="mr-3 text-2xl" />
            Today's Date: {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>

          {loading ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600 text-xl flex flex-col items-center justify-center">
              <FontAwesomeIcon icon={faSpinner} spin className="text-indigo-500 text-5xl mb-4" />
              <p>Loading your previous reservations...</p>
            </div>
          ) : previousReservations.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600 text-xl">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-400 text-5xl mb-4" />
              <p>You have no previous reservations.</p>
              <p className="mt-2 text-lg">Start exploring properties to make a new reservation!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {previousReservations.map((reservation) => (
                <div
                  key={reservation.room_id} // Assuming room_id is unique
                  className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 p-6 space-y-3 flex flex-col"
                >
                  <h3 className="text-2xl font-bold text-indigo-700 mb-3 border-b-2 border-indigo-100 pb-2 flex items-center">
                    <FontAwesomeIcon icon={faHome} className="mr-3 text-2xl" />
                    House: {reservation.house_no}
                  </h3>
                  <div className="space-y-2">
                    <p className="text-md">
                      <span className="font-medium text-gray-600">
                        <FontAwesomeIcon icon={faUserCircle} className="mr-2 text-blue-400" />User Name:
                      </span>{" "}
                      <span className="font-semibold text-gray-800">{reservation.user_name}</span>
                    </p>
                    <p className="text-md">
                      <span className="font-medium text-gray-600">
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-blue-400" />Email:
                      </span>{" "}
                      <span className="font-semibold text-gray-800">{reservation.email}</span>
                    </p>
                    <p className="text-md">
                      <span className="font-medium text-gray-600">
                        <FontAwesomeIcon icon={faPhone} className="mr-2 text-blue-400" />Phone:
                      </span>{" "}
                      <span className="font-semibold text-gray-800">{reservation.phone}</span>
                    </p>
                    <p className="text-md">
                      <span className="font-medium text-gray-600">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-blue-400" />Entry Date:
                      </span>{" "}
                      <span className="font-semibold text-gray-800">{new Date(reservation.entrydate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </p>
                    <p className="text-md">
                      <span className="font-medium text-gray-600">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-blue-400" />Deposit Paid:
                      </span>{" "}
                      <span className="font-semibold text-green-600">KSh {parseFloat(reservation.deposit_paid).toLocaleString()}</span>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(reservation.booking_status)}`}>
                      <FontAwesomeIcon icon={faCircle} className="mr-2 text-xs" />
                      Booking: {reservation.booking_status}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(reservation.payment_status)}`}>
                      <FontAwesomeIcon icon={faCircle} className="mr-2 text-xs" />
                      Payment: {reservation.payment_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AuthGuard>
  );
};

export default PreviousReservationsPage;